import { assertPrecondition } from '../../errors';
import { isMfsCollection } from '../mfsTypeGuard';
import { MfsCollection, MfsItem, AppDataOfCollection } from '../mfsTypes';

/** The internal values property of a collection. Needed so that we don't conflict with possible app-defined values properties. */
export const values = Symbol('values');

/** The internal representation used during item creation, with additional values property. */
export type MfsCollectionInternal<T extends MfsCollection> = AppDataOfCollection<T> & { [values]: string[] };

/**
 * Access the internal values property from the base {@link MfsItem} type. This method is called only from the common
 * path on createItemInternal, and is guaranteed to be a {@link MfsCollectionInternal}.
 */
export function getCollectionValues<T extends MfsItem>(item: T): string[] {
  assertPrecondition(isMfsCollection(item));
  const collectionValues = (item as unknown as MfsCollectionInternal<MfsCollection>)[values];
  assertPrecondition(!!collectionValues);
  return collectionValues;
}

/**
 * Omits the internal values property from the given collection when returning the result to the app code. Only called
 * from the createCollection API.
 */
export function omitCollectionValues<T extends MfsCollection>(item: T): T {
  const i: Partial<MfsCollectionInternal<T>> = item as unknown as MfsCollectionInternal<T>;
  delete i[values];
  assertPrecondition(isMfsCollection(i));
  return i as unknown as T;
}
